/**
 * Call Conversion Dashboard
 * Track opportunity calls and conversion rates with detailed drill-down
 * Shows: Positive (booked), Negative (missed), Neutral (non-opportunity)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Phone, Filter, Download, ChevronDown, ChevronUp, Search, SlidersHorizontal, X } from 'lucide-react';
import { CallRecordingPlayer } from '@/components/agent-console/CallRecordingPlayer';
import { TranscriptViewer } from '@/components/agent-console/TranscriptViewer';
import { CoachingNotes } from '@/components/agent-console/CoachingNotes';

interface CallConversionData {
  id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  started_at: string;
  duration_seconds: number;
  company_name?: string;
  agent_user_id?: string;
  marketing_source?: string;
  is_existing_customer?: boolean;
  outcome?: {
    outcome_type: 'opportunity_booked' | 'opportunity_missed' | 'neutral' | 'incomplete';
    was_opportunity: boolean;
    was_booked: boolean;
    ai_analysis_summary?: string;
    script_adherence_score?: number;
  };
  recording?: {
    recording_url: string;
    recording_duration_seconds: number;
  };
  transcript?: {
    full_transcript: string;
    agent_transcript?: string;
    customer_transcript?: string;
  };
  coaching_notes?: any[];
}

export default function CallConversions() {
  const [dateRange, setDateRange] = useState('today');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [companies, setCompanies] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [calls, setCalls] = useState<CallConversionData[]>([]);
  const [filteredCalls, setFilteredCalls] = useState<CallConversionData[]>([]);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Quick Filters
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['call']); // TODO: Add 'text', 'voicemail' when available
  const [callDirection, setCallDirection] = useState<string>('both'); // 'inbound', 'outbound', 'both'
  const [answeredState, setAnsweredState] = useState<string[]>([]); // 'answered', 'missed'
  const [graderOutcome, setGraderOutcome] = useState<string[]>([]); // 'positive', 'negative', 'neutral', 'unworked', 'filtered'
  const [phoneSearch, setPhoneSearch] = useState('');

  // Advanced Filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [opportunityOverlay, setOpportunityOverlay] = useState<string[]>([]); // 'positive', 'negative', 'none'
  const [opportunityScoreRange, setOpportunityScoreRange] = useState([0, 100]);
  const [bookingStatus, setBookingStatus] = useState<string[]>([]); // 'booked', 'not_booked', 'quote_sent', 'no_show', 'cancelled'
  const [ctaAttempted, setCtaAttempted] = useState<string>('all'); // 'yes', 'no', 'all'
  const [dmPathSecured, setDmPathSecured] = useState<string>('all'); // 'yes', 'no', 'all'
  const [addOnDetected, setAddOnDetected] = useState<string>('all'); // 'yes', 'no', 'all'
  const [scriptScoreRange, setScriptScoreRange] = useState([0, 100]);
  const [criticalViolations, setCriticalViolations] = useState<string[]>([]); // 'greeting', 'value_price', 'name_address', 'callback', 'emergency', 'compliance'
  const [checklistMisses, setChecklistMisses] = useState<string[]>([]); // 'marketing_source', 'upsell', 'appointment_windows', 'objection_match'
  const [objectionTypes, setObjectionTypes] = useState<string[]>([]); // 'price', 'timing', 'authority', 'vendor', 'trust', 'other'
  const [sentimentDelta, setSentimentDelta] = useState<string>('all'); // 'improved', 'unchanged', 'worsened', 'all'
  const [talkListenRatioRange, setTalkListenRatioRange] = useState([0, 100]);
  const [durationRange, setDurationRange] = useState([0, 3600]); // seconds
  const [holdSilenceRange, setHoldSilenceRange] = useState([0, 100]); // percentage
  const [marketingSource, setMarketingSource] = useState('all'); // TODO: Implement campaign names
  const [dialedNumber, setDialedNumber] = useState<string[]>([]); // TODO: Implement DNIS multi-select
  const [callerType, setCallerType] = useState<string>('all'); // 'first_time', 'returning_30', 'returning_90', 'returning_365', 'all'
  const [memberStatus, setMemberStatus] = useState<string>('all'); // 'member', 'non_member', 'all'

  // Metrics
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    opportunityCalls: 0,
    positiveCalls: 0,
    negativeCalls: 0,
    neutralCalls: 0,
    conversionRate: 0
  });

  useEffect(() => {
    loadCompanies();
    loadAgents();
    loadCalls();
  }, [dateRange, customDateStart, customDateEnd]);

  useEffect(() => {
    filterCalls();
  }, [calls, selectedCompany, selectedAgent, phoneSearch, graderOutcome, callDirection, scriptScoreRange, durationRange]);

  const loadCompanies = async () => {
    const { data } = await supabase
      .from('twilio_phone_numbers')
      .select('company_name, phone_number')
      .eq('is_active', true);
    
    // Add RAHS Warranty and ensure unique companies
    const companySet = new Set((data || []).map(c => c.company_name));
    companySet.add('RAHS Warranty');
    companySet.add('Hales AC');
    companySet.add('Climate Control');
    companySet.add('Abraham AC');
    companySet.add('Unknown');
    
    const uniqueCompanies = Array.from(companySet).map(name => ({
      company_name: name,
      phone_number: null
    }));
    
    setCompanies(uniqueCompanies);
  };

  const loadAgents = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, first_name, last_name')
      .order('first_name');
    setAgents(data || []);
  };

  const loadCalls = async () => {
    setLoading(true);
    try {
      const dateFilter = getDateFilter();

      // Query call_conversions table (TODO: Once view is created, switch to call_conversions_filterable)
      const { data, error } = await supabase
        .from('calls')
        .select(`
          *,
          call_outcomes(*),
          call_recordings(*),
          call_transcripts(*),
          call_coaching_notes(*)
        `)
        .gte('started_at', dateFilter.start)
        .lte('started_at', dateFilter.end)
        .order('started_at', { ascending: false })
        .limit(500);

      if (error) throw error;

      console.log('Loading calls, raw data count:', data?.length || 0);
      
      // Transform and enrich data
      const enriched: CallConversionData[] = (data || []).map((call: any) => {
        // Find company from phone number
        const company = companies.find(c => c.phone_number === call.to_number);
        
        // Get first outcome if exists
        const outcome = call.call_outcomes?.[0];

        return {
          id: call.id,
          call_sid: call.call_sid,
          from_number: call.from_number,
          to_number: call.to_number,
          started_at: call.started_at,
          duration_seconds: call.duration_seconds,
          company_name: call.company_name || company?.company_name,
          agent_user_id: call.agent_user_id,
          marketing_source: call.marketing_source || 'Unknown',
          is_existing_customer: call.is_member || false,
          outcome: outcome ? {
            outcome_type: outcome.outcome_type,
            was_opportunity: outcome.was_opportunity,
            was_booked: outcome.was_booked,
            ai_analysis_summary: outcome.ai_analysis_summary,
            script_adherence_score: outcome.script_adherence_score
          } : undefined,
          recording: call.call_recordings?.[0],
          transcript: call.call_transcripts?.[0],
          coaching_notes: call.call_coaching_notes
        };
      });

      console.log('Enriched calls count:', enriched.length);
      setCalls(enriched);
    } catch (error) {
      console.error('Error loading calls:', error);
      console.error('Error details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateFilter = () => {
    // Use custom dates if provided
    if (dateRange === 'custom' && customDateStart && customDateEnd) {
      const start = new Date(customDateStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(customDateEnd);
      end.setHours(23, 59, 59, 999);
      return { start: start.toISOString(), end: end.toISOString() };
    }

    const end = new Date();
    const start = new Date();

    switch (dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case '7days':
        start.setDate(start.getDate() - 7);
        break;
      case '30days':
        start.setDate(start.getDate() - 30);
        break;
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        break;
    }

    return { start: start.toISOString(), end: end.toISOString() };
  };

  const filterCalls = () => {
    let filtered = [...calls];

    // Company filter
    if (selectedCompany !== 'all') {
      filtered = filtered.filter(c => c.company_name === selectedCompany);
    }

    // Agent filter
    if (selectedAgent !== 'all') {
      filtered = filtered.filter(c => c.agent_user_id === selectedAgent);
    }

    // Phone search filter
    if (phoneSearch) {
      filtered = filtered.filter(c => 
        c.from_number.includes(phoneSearch) || c.to_number.includes(phoneSearch)
      );
    }

    // Grader outcome filter
    if (graderOutcome.length > 0) {
      filtered = filtered.filter(c => {
        if (!c.outcome) return false;
        if (graderOutcome.includes('positive') && c.outcome.outcome_type === 'opportunity_booked') return true;
        if (graderOutcome.includes('negative') && c.outcome.outcome_type === 'opportunity_missed') return true;
        if (graderOutcome.includes('neutral') && c.outcome.outcome_type === 'neutral') return true;
        return false;
      });
    }

    // Call direction filter (TODO: Backend needs direction field)
    if (callDirection !== 'both') {
      console.log('TODO: Filter by call direction -', callDirection, '- backend implementation needed');
      // Will filter once backend provides direction field
    }

    // Script score range filter
    if (scriptScoreRange[1] < 100 || scriptScoreRange[0] > 0) {
      filtered = filtered.filter(c => {
        const score = c.outcome?.script_adherence_score;
        if (score === undefined) return false;
        return score >= scriptScoreRange[0] && score <= scriptScoreRange[1];
      });
    }

    // Duration range filter (TODO: Backend implementation)
    if (durationRange[1] < 3600) {
      filtered = filtered.filter(c => {
        return c.duration_seconds <= durationRange[1];
      });
    }

    // TODO: Apply other advanced filters when backend provides data
    if (criticalViolations.length > 0) {
      console.log('TODO: Filter by critical violations -', criticalViolations, '- backend implementation needed');
    }
    if (objectionTypes.length > 0) {
      console.log('TODO: Filter by objection types -', objectionTypes, '- backend implementation needed');
    }

    setFilteredCalls(filtered);
    calculateMetrics(filtered);
  };

  const calculateMetrics = (callList: CallConversionData[]) => {
    const total = callList.length;
    
    // Debug: Log sample of call outcomes
    console.log('=== METRICS CALCULATION DEBUG ===');
    console.log('Total calls:', total);
    console.log('Sample calls with outcomes:', callList.slice(0, 5).map(c => ({
      id: c.id,
      outcome_type: c.outcome?.outcome_type,
      was_opportunity: c.outcome?.was_opportunity,
      was_booked: c.outcome?.was_booked
    })));
    
    // Business Logic Requirements:
    // 1. Opportunity Calls = was_opportunity: true
    // 2. Positive = was_opportunity: true AND outcome_type: 'opportunity_booked'
    // 3. Negative = was_opportunity: true AND outcome_type: 'opportunity_missed'
    // 4. Neutral = ALL non-opportunity calls (was_opportunity: false OR no outcome)
    // 5. Total = Positive + Negative + Neutral
    // 6. Opportunity = Positive + Negative
    
    // Positive calls: opportunity that was booked
    const positiveCalls = callList.filter(c => 
      c.outcome?.was_opportunity === true && 
      c.outcome?.outcome_type === 'opportunity_booked'
    );
    const positive = positiveCalls.length;
    
    // Negative calls: opportunity that was missed
    const negativeCalls = callList.filter(c => 
      c.outcome?.was_opportunity === true && 
      c.outcome?.outcome_type === 'opportunity_missed'
    );
    const negative = negativeCalls.length;
    
    // Neutral calls: ALL non-opportunity calls
    const neutralCalls = callList.filter(c => 
      !c.outcome || c.outcome?.was_opportunity === false
    );
    const neutral = neutralCalls.length;
    
    // Opportunity calls = sum of positive + negative
    const opportunity = positive + negative;
    
    // Conversion rate = positive / opportunity
    const conversion = opportunity > 0 ? ((positive / opportunity) * 100).toFixed(1) : '0.0';
    
    console.log('Positive calls (opportunity + booked):', positive);
    console.log('Negative calls (opportunity + missed):', negative);
    console.log('Neutral calls (non-opportunity):', neutral);
    console.log('Opportunity calls (positive + negative):', opportunity);
    console.log('Conversion rate:', conversion + '%');
    
    // Sanity checks
    const calculatedTotal = positive + negative + neutral;
    if (calculatedTotal !== total) {
      console.warn('⚠️ SANITY CHECK FAILED: positive + negative + neutral !== total');
      console.warn(`  ${positive} + ${negative} + ${neutral} = ${calculatedTotal}, but total = ${total}`);
      console.warn('Missing calls:', total - calculatedTotal);
      
      // Find calls that don't fit any category
      const uncategorized = callList.filter(c => {
        if (!c.outcome) return false; // These are counted as neutral
        const isPositive = c.outcome.was_opportunity === true && c.outcome.outcome_type === 'opportunity_booked';
        const isNegative = c.outcome.was_opportunity === true && c.outcome.outcome_type === 'opportunity_missed';
        const isNeutral = c.outcome.was_opportunity === false;
        return !isPositive && !isNegative && !isNeutral;
      });
      
      console.warn('Uncategorized calls:', uncategorized.map(c => ({
        id: c.id,
        outcome_type: c.outcome?.outcome_type,
        was_opportunity: c.outcome?.was_opportunity
      })));
    }
    console.log('=================================');

    setMetrics({
      totalCalls: total,
      opportunityCalls: opportunity,
      positiveCalls: positive,
      negativeCalls: negative,
      neutralCalls: neutral,
      conversionRate: parseFloat(conversion)
    });
  };

  const exportToCSV = () => {
    const csv = [
      ['Date', 'Time', 'Company', 'Number', 'Duration', 'Classification', 'Outcome', 'Conversion', 'Script Score', 'Source', 'Existing Customer'].join(','),
      ...filteredCalls.map(call => [
        new Date(call.started_at).toLocaleDateString(),
        new Date(call.started_at).toLocaleTimeString(),
        call.company_name || 'Unknown',
        call.from_number,
        call.duration_seconds,
        call.outcome?.was_opportunity ? 'Opportunity' : 'Neutral',
        call.outcome?.outcome_type || 'N/A',
        call.outcome?.was_booked ? 'Positive' : call.outcome?.was_opportunity ? 'Negative' : 'Neutral',
        call.outcome?.script_adherence_score || 'N/A',
        call.marketing_source || 'Unknown',
        call.is_existing_customer ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-conversions-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getOutcomeIcon = (outcome?: CallConversionData['outcome']) => {
    if (!outcome) return '⚪️';
    if (outcome.outcome_type === 'opportunity_booked') return '🟢';
    if (outcome.outcome_type === 'opportunity_missed') return '🔴';
    if (outcome.outcome_type === 'neutral') return '🟡';
    return '⚪️';
  };

  const getOutcomeColors = (outcome?: CallConversionData['outcome']) => {
    if (!outcome) return { border: 'border-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-800' };
    if (outcome.outcome_type === 'opportunity_booked') return { border: 'border-green-500', bg: 'bg-green-50', badge: 'bg-green-100 text-green-800' };
    if (outcome.outcome_type === 'opportunity_missed') return { border: 'border-red-500', bg: 'bg-red-50', badge: 'bg-red-100 text-red-800' };
    if (outcome.outcome_type === 'neutral') return { border: 'border-yellow-500', bg: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800' };
    return { border: 'border-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="📊 Call Conversion Dashboard"
      subtitle="Track opportunity calls and conversion rates"
      actions={
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      }
    >
      {/* Key Metrics */}
      <StatsGrid cols={6}>
        <StatCard
          label="Total Incoming Calls"
          value={metrics.totalCalls}
          icon={Phone}
        />
        <StatCard
          label="Opportunity Calls"
          value={metrics.opportunityCalls}
          icon={TrendingUp}
          subtitle={`${metrics.totalCalls > 0 ? ((metrics.opportunityCalls / metrics.totalCalls) * 100).toFixed(1) : '0.0'}% of calls`}
        />
        <StatCard
          label="Positive (Booked)"
          value={metrics.positiveCalls}
          icon={TrendingUp}
          variant="success"
        />
        <StatCard
          label="Negative (Missed)"
          value={metrics.negativeCalls}
          icon={TrendingDown}
          variant="critical"
        />
        <StatCard
          label="Neutral (Non-Opp)"
          value={metrics.neutralCalls}
          icon={Minus}
        />
        <StatCard
          label="Conversion Rate"
          value={`${metrics.conversionRate}%`}
          icon={TrendingUp}
          variant={metrics.conversionRate >= 75 ? 'success' : metrics.conversionRate >= 50 ? 'warning' : 'critical'}
          subtitle={`${metrics.positiveCalls} of ${metrics.opportunityCalls} opp.`}
        />
      </StatsGrid>

      {/* Quick Filters Row */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Date:</span>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomDateStart('');
                  setCustomDateEnd('');
                }
              }}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                />
                <span className="text-gray-500 text-sm">to</span>
                <input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                />
              </>
            )}
          </div>

          {/* Company Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Company:</span>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="all">All Companies</option>
              {companies.map(c => (
                <option key={c.phone_number} value={c.company_name}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* Agent Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Agent:</span>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.first_name} {agent.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Channel Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Channel:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedChannels(['call'])}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedChannels.includes('call')
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                📞 Call
              </button>
              <button
                disabled
                className="px-3 py-1.5 text-sm rounded-full border bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                title="Coming soon"
              >
                💬 Text
              </button>
              <button
                disabled
                className="px-3 py-1.5 text-sm rounded-full border bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                title="Coming soon"
              >
                📧 Voicemail
              </button>
            </div>
          </div>

          {/* Call Direction */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Direction:</span>
            <div className="flex gap-1 border rounded-lg p-1 bg-gray-50">
              <button
                onClick={() => setCallDirection('inbound')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  callDirection === 'inbound'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Inbound
              </button>
              <button
                onClick={() => setCallDirection('outbound')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  callDirection === 'outbound'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Outbound
              </button>
              <button
                onClick={() => setCallDirection('both')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  callDirection === 'both'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Both
              </button>
            </div>
          </div>

          {/* Grader Outcome */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Outcome:</span>
            <div className="flex gap-2">
              {[
                { value: 'positive', label: 'Positive' },
                { value: 'negative', label: 'Negative' },
                { value: 'neutral', label: 'Neutral' }
              ].map(outcome => (
                <button
                  key={outcome.value}
                  onClick={() => {
                    if (graderOutcome.includes(outcome.value)) {
                      setGraderOutcome(graderOutcome.filter(o => o !== outcome.value));
                    } else {
                      setGraderOutcome([...graderOutcome, outcome.value]);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    graderOutcome.includes(outcome.value)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {outcome.label}
                </button>
              ))}
            </div>
          </div>

          {/* Phone Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              placeholder="Search by phone..."
              className="pl-10 pr-4 py-2 border rounded-lg bg-white text-sm w-48"
            />
          </div>

          {/* Advanced Filters Button */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ml-auto ${
              showAdvanced
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Advanced
          </button>
        </div>
      </div>

      {/* Advanced Filters Drawer */}
      {showAdvanced && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowAdvanced(false)}
          />
          
          {/* Drawer */}
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Advanced Filters</h2>
                <p className="text-sm text-gray-500 mt-1">Refine your search with detailed criteria</p>
              </div>
              <button
                onClick={() => setShowAdvanced(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-8">
              {/* Note about backend implementation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> These advanced filters are UI-ready but require backend implementation. 
                  See CALL_CONVERSIONS_FILTERS_IMPLEMENTATION.md for details.
                </p>
              </div>

              {/* Apply & Clear Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // TODO: Apply filters
                    console.log('Applying advanced filters...');
                    setShowAdvanced(false);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    // Reset all advanced filters
                    setOpportunityOverlay([]);
                    setOpportunityScoreRange([0, 100]);
                    setBookingStatus([]);
                    setCtaAttempted('all');
                    setDmPathSecured('all');
                    setAddOnDetected('all');
                    setScriptScoreRange([0, 100]);
                    setCriticalViolations([]);
                    setChecklistMisses([]);
                    setObjectionTypes([]);
                    setSentimentDelta('all');
                    setTalkListenRatioRange([0, 100]);
                    setDurationRange([0, 3600]);
                    setHoldSilenceRange([0, 100]);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Clear All
                </button>
              </div>

              {/* Opportunity & Conversion Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Opportunity & Conversion
                </h3>
                <div className="space-y-4">
                  {/* Opportunity Overlay Grade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opportunity Overlay Grade
                    </label>
                    <div className="flex gap-2">
                      {['positive', 'negative', 'none'].map(grade => (
                        <button
                          key={grade}
                          onClick={() => {
                            if (opportunityOverlay.includes(grade)) {
                              setOpportunityOverlay(opportunityOverlay.filter(g => g !== grade));
                            } else {
                              setOpportunityOverlay([...opportunityOverlay, grade]);
                            }
                          }}
                          className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                            opportunityOverlay.includes(grade)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {grade.charAt(0).toUpperCase() + grade.slice(1)}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">TODO: Backend implementation needed</p>
                  </div>

                  {/* Script Score Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Script Adherence Score: {scriptScoreRange[0]}% - {scriptScoreRange[1]}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={scriptScoreRange[1]}
                      onChange={(e) => setScriptScoreRange([0, parseInt(e.target.value)])}
                      className="w-full"
                    />
                  </div>

                  {/* Duration Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Call Duration: {Math.floor(durationRange[0] / 60)}m - {Math.floor(durationRange[1] / 60)}m
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="3600"
                      step="60"
                      value={durationRange[1]}
                      onChange={(e) => setDurationRange([0, parseInt(e.target.value)])}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">TODO: Backend implementation needed</p>
                  </div>
                </div>
              </div>

              {/* Script & Quality Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Script & Quality Metrics
                </h3>
                <div className="space-y-4">
                  {/* Critical Violations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Critical Violations
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'greeting', label: 'Greeting Missing' },
                        { value: 'value_price', label: 'Value-Before-Price' },
                        { value: 'name_address', label: 'Name/Address Missing' },
                        { value: 'callback', label: 'Callback Info Missing' },
                        { value: 'emergency', label: 'Emergency Ignored' },
                        { value: 'compliance', label: 'Compliance Missing' }
                      ].map(violation => (
                        <label key={violation.value} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={criticalViolations.includes(violation.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCriticalViolations([...criticalViolations, violation.value]);
                              } else {
                                setCriticalViolations(criticalViolations.filter(v => v !== violation.value));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{violation.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">TODO: Backend implementation needed</p>
                  </div>

                  {/* Objection Types */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objection Types Detected
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['Price', 'Timing', 'Authority', 'Vendor', 'Trust', 'Other'].map(type => (
                        <button
                          key={type}
                          onClick={() => {
                            const value = type.toLowerCase();
                            if (objectionTypes.includes(value)) {
                              setObjectionTypes(objectionTypes.filter(t => t !== value));
                            } else {
                              setObjectionTypes([...objectionTypes, value]);
                            }
                          }}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            objectionTypes.includes(type.toLowerCase())
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">TODO: Backend implementation needed</p>
                  </div>
                </div>
              </div>

              {/* Marketing & Attribution Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
                  Marketing & Attribution
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marketing Source
                    </label>
                    <select
                      value={marketingSource}
                      onChange={(e) => setMarketingSource(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg bg-white"
                    >
                      <option value="all">All Sources</option>
                      <option value="google">Google Ads</option>
                      <option value="facebook">Facebook</option>
                      <option value="organic">Organic</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">TODO: Load from campaigns table</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caller Type
                    </label>
                    <div className="flex gap-2">
                      {[
                        { value: 'first_time', label: 'First Time' },
                        { value: 'returning_30', label: 'Return 30d' },
                        { value: 'returning_90', label: 'Return 90d' }
                      ].map(type => (
                        <button
                          key={type.value}
                          onClick={() => setCallerType(type.value)}
                          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                            callerType === type.value
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">TODO: Backend implementation needed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with Apply button */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => {
                  console.log('Applying filters...');
                  setShowAdvanced(false);
                }}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Call List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Call Details</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredCalls.length} incoming calls
            </p>
          </div>
        </div>

        <div className="space-y-3 max-h-[1000px] overflow-y-auto">
          {filteredCalls.map((call) => {
            const isExpanded = expandedCall === call.call_sid;
            const colors = getOutcomeColors(call.outcome);
            const icon = getOutcomeIcon(call.outcome);

            return (
              <div
                key={call.id}
                className={`border-2 rounded-lg overflow-hidden ${colors.border} ${colors.bg}`}
              >
                {/* Call Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setExpandedCall(isExpanded ? null : call.call_sid)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-2xl">{icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-900">
                            {call.from_number}
                          </span>
                          {call.outcome && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>
                              {call.outcome.was_opportunity
                                ? call.outcome.was_booked ? 'Positive - Booked' : 'Negative - Missed'
                                : 'Neutral - No Opportunity'
                              }
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{call.company_name || 'Unknown'}</span>
                          <span>{new Date(call.started_at).toLocaleString()}</span>
                          <span>{Math.floor((call.duration_seconds || 0) / 60)}:{((call.duration_seconds || 0) % 60).toString().padStart(2, '0')}</span>
                          {call.marketing_source && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                              {call.marketing_source}
                            </span>
                          )}
                          {call.is_existing_customer && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded">
                              Existing Customer
                            </span>
                          )}
                        </div>

                        {call.outcome?.ai_analysis_summary && (
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                            {call.outcome.ai_analysis_summary}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Script Adherence Score */}
                    {call.outcome?.script_adherence_score !== undefined && (
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs text-gray-500">Script Score</span>
                        <span className={`text-lg font-bold ${
                          call.outcome.script_adherence_score >= 90 ? 'text-green-600' :
                          call.outcome.script_adherence_score >= 80 ? 'text-blue-600' :
                          call.outcome.script_adherence_score >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {call.outcome.script_adherence_score}%
                        </span>
                      </div>
                    )}

                    <button className="ml-3">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-300 p-4 space-y-4 bg-white">
                    {/* Recording */}
                    {call.recording && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Call Recording</h4>
                        <CallRecordingPlayer
                          recordingUrl={call.recording.recording_url}
                          duration={call.recording.recording_duration_seconds}
                          callSid={call.call_sid}
                        />
                      </div>
                    )}

                    {/* Transcript */}
                    {call.transcript && (
                      <TranscriptViewer
                        fullTranscript={call.transcript.full_transcript}
                        agentTranscript={call.transcript.agent_transcript}
                        customerTranscript={call.transcript.customer_transcript}
                      />
                    )}

                    {/* Coaching Notes */}
                    {call.coaching_notes && call.coaching_notes.length > 0 && (
                      <CoachingNotes notes={call.coaching_notes} />
                    )}

                    {/* Call Metadata */}
                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Call SID:</span>
                        <span className="ml-2 font-mono text-xs">{call.call_sid}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Duration:</span>
                        <span className="ml-2 font-semibold">{Math.floor((call.duration_seconds || 0) / 60)}m {((call.duration_seconds || 0) % 60)}s</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Marketing Source:</span>
                        <span className="ml-2 font-semibold">{call.marketing_source || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Customer Type:</span>
                        <span className="ml-2 font-semibold">
                          {call.is_existing_customer ? 'Existing' : 'New Lead'}
                        </span>
                      </div>
                      {call.outcome && (
                        <>
                          <div>
                            <span className="text-gray-600">Opportunity:</span>
                            <span className="ml-2 font-semibold">
                              {call.outcome.was_opportunity ? 'Yes' : 'No'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Booked:</span>
                            <span className="ml-2 font-semibold">
                              {call.outcome.was_booked ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filteredCalls.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Phone className="w-12 h-12 mx-auto mb-3" />
              <p>No calls found for selected filters</p>
            </div>
          )}
        </div>
      </Card>
    </AdminPageLayout>
  );
}
